
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Form } from '@/components/ui/form';
import { useToast } from '@/hooks/use-toast';
import { useCreateHarvest, useUpdateHarvest, useGetHarvestById } from '@/hooks/use-harvest';
import { useGetFarmById } from '@/hooks/use-farm';
import { Harvest } from '@/types/database.types';
import { harvestFormSchema, type HarvestFormValues } from './form/schema';
import BasicInfoSection from './form/BasicInfoSection';
import ProgressSection from './form/ProgressSection';
import QualitySection from './form/QualitySection';
import NotesField from './form/NotesField';
import FormActions from './form/FormActions';

interface HarvestFormProps {
  harvestId?: string;
  farmId?: string;
}

const HarvestForm: React.FC<HarvestFormProps> = ({ harvestId, farmId }) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const isEditMode = !!harvestId;

  const { data: harvest, isLoading: isHarvestLoading } = useGetHarvestById(harvestId || '');
  const { data: farm, isLoading: isFarmLoading } = useGetFarmById(farmId || (harvest?.farm_id || ''));
  const createHarvest = useCreateHarvest();
  const updateHarvest = useUpdateHarvest();

  // Initialize the form
  const form = useForm<HarvestFormValues>({
    resolver: zodResolver(harvestFormSchema),
    defaultValues: {
      farm_id: farmId || '',
      status: 'planned',
      expected_date: null,
      actual_date: null,
      expected_quantity: null,
      actual_quantity: null,
      quality_grade: null,
      notes: null,
      product_name: null,
    },
  });

  // Set farm data as default when available
  useEffect(() => {
    if (farm && !isEditMode) {
      // Always ensure farm_id is set
      form.setValue('farm_id', farm.id);
      
      // Pre-fill expected date with farm's harvest_start_date if available
      if (farm.harvest_start_date) {
        form.setValue('expected_date', farm.harvest_start_date);
      }
      
      // Pre-fill expected quantity with farm's estimated_harvest_kg if available
      if (farm.estimated_harvest_kg) {
        form.setValue('expected_quantity', farm.estimated_harvest_kg);
      }

      // Pre-fill product_name from farm if available
      if (farm.product_name) {
        form.setValue('product_name', farm.product_name);
      }
    }
  }, [farm, form, isEditMode]);

  // Populate form when editing an existing harvest
  useEffect(() => {
    if (isEditMode && harvest) {
      form.reset({
        farm_id: harvest.farm_id,
        status: harvest.status as 'planned' | 'in_progress' | 'completed' | 'cancelled',
        expected_date: harvest.expected_date,
        actual_date: harvest.actual_date,
        expected_quantity: harvest.expected_quantity,
        actual_quantity: harvest.actual_quantity,
        quality_grade: harvest.quality_grade,
        notes: harvest.notes,
        product_name: harvest.product_name,
      });
    }
  }, [isEditMode, harvest, form]);

  const onSubmit = async (values: HarvestFormValues) => {
    try {
      if (isEditMode && harvestId) {
        await updateHarvest.mutateAsync({ 
          id: harvestId, 
          data: values 
        });
        toast({
          title: "Success",
          description: "Harvest has been updated successfully.",
        });
      } else {
        // Ensure farm_id is set before creating
        if (!values.farm_id && farmId) {
          values.farm_id = farmId;
        }
        
        // Validate that farm_id is present
        if (!values.farm_id) {
          toast({
            title: "Error",
            description: "Farm ID is required to create a harvest.",
            variant: "destructive",
          });
          return;
        }
        
        // If no product_name is provided but farm has one, use farm's product_name
        if (!values.product_name && farm?.product_name) {
          values.product_name = farm.product_name;
        }
        
        // Create a new object with the required properties for Harvest type
        await createHarvest.mutateAsync(values);
        toast({
          title: "Success",
          description: "Harvest has been created successfully.",
        });
      }
      
      // Redirect based on context
      if (farmId) {
        navigate(`/farms/${farmId}`);
      } else {
        navigate('/harvests');
      }
    } catch (error) {
      console.error('Error saving harvest:', error);
      toast({
        title: "Error",
        description: "Failed to save harvest. Please try again.",
        variant: "destructive"
      });
    }
  };

  if ((isHarvestLoading && isEditMode) || (isFarmLoading && farmId)) {
    return <div className="flex justify-center p-8"><Loader2 className="h-8 w-8 animate-spin" /></div>;
  }

  // Determine if we need to show the farm ID field
  const showFarmIdField = !farmId && !harvest?.farm_id;

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <BasicInfoSection 
          form={form} 
          isEditMode={isEditMode} 
          showFarmIdField={showFarmIdField} 
        />
        
        <ProgressSection form={form} />
        
        <QualitySection form={form} />
        
        <NotesField form={form} />
        
        <FormActions 
          form={form} 
          isEditMode={isEditMode} 
          farmId={farmId} 
        />
      </form>
    </Form>
  );
};

export default HarvestForm;
